import SelectInput from 'CoreComponents/selectInput/';
import TimetableSlot from './timetableSlot';

import PhotographerAvailabilityAdapter from '../../adapters/photographerAvailability';
import * as customEvent from './events';

// Default timetable form selector
const DEFAULT_SELECTOR = '.form-availability-timetable';

const SELECTOR = {
  photographerIdInput: '[name="cart[photographer_id]"]',
  calendarInput: '.form-availability-timetable__select-date',
  packageSelect: '.form-availability-timetable__select-package',
  packageSelectInput: '.form-availability-timetable .select',
  slotList: '.form-availability-timetable__list-slot',
  slotItem: '.form-availability-timetable__slot .form__radio--tag',
  submitButton: '.form-availability-timetable__action button[type="submit"]'
};

const CLASS_NAME = {
  fetching: 'form-availability-timetable--fetching',
  error: 'form-availability-timetable--error'
};

class AvailabilityTimetableForm {
  constructor(elementRef) {
    // Initialise attributes
    this.elementRef = elementRef || document.querySelector(DEFAULT_SELECTOR);
    this.calendarInput = this.elementRef.querySelector(SELECTOR['calendarInput']);
    this.packageSelects = this.elementRef.querySelectorAll(SELECTOR['packageSelect']);
    this.slotList = this.elementRef.querySelector(SELECTOR['slotList']);
    this.submitButton = document.querySelector(SELECTOR['submitButton']);

    this.state = {
      slotSelected: false
    };

    // Bind functions
    this.changePackageOrDateHandler = this.changePackageOrDateHandler.bind(this);
    this.onClickSlotItemHandler = this.onClickSlotItemHandler.bind(this);
    this.onSubmitHandler = this.onSubmitHandler.bind(this);

    this.onFetchingHandler = this.onFetchingHandler.bind(this);
    this.onFetchSuccessHandler = this.onFetchSuccessHandler.bind(this);
    this.onFetchFailureHandler = this.onFetchFailureHandler.bind(this);

    this.onSelectCategoryHandler = this.onSelectCategoryHandler.bind(this);
    this.onSelectPackageHandler = this.onSelectPackageHandler.bind(this);
    this.onSelectSlotHandler = this.onSelectSlotHandler.bind(this);
    this.onUnselectSlotHandler = this.onUnselectSlotHandler.bind(this);
    this.submitButton.addEventListener('click', this.onSubmitHandler);

    this._setup();
    this._addEventListeners();

    // Initial fetching of availability on component mount
    this._fetchPhotographerAvailability();
  }

  // Event Handlers

  /**
   * Event handler to handle package or date changes.
   * Trigger fetch of a photographer availability.
   *
   * @param {Event} event - `change` event
   * */
  changePackageOrDateHandler(event) {
    this._fetchPhotographerAvailability();
  }

  /**
   * Event handler when an available slot is clicked.
   *
   * @param {Event} event - `click` event.
   * */
  onClickSlotItemHandler(event) {
    this.elementRef.dispatchEvent(new CustomEvent(customEvent.PHOTOGRAPHER_AVAILABILITIES_SELECT_SLOT));
  }

  /**
   * Event handler when the form is submitted.
   *
   * @param {Event} event - `submit` event.
   * */
  onSubmitHandler(event) {
    this.elementRef.submit();
    this._disableFormSubmit();
  }

  // Custom Event Handlers

  /**
   * Event handler when fetching a photographer availability information.
   *
   * @param {Event} _event - fetch start event.
   * */
  onFetchingHandler(_event) {
    this._showSpinner();
    this._hideError();

    this.elementRef.dispatchEvent(new CustomEvent(customEvent.PHOTOGRAPHER_AVAILABILITIES_UNSELECT_SLOT));
  }

  /**
   * Event handler when a photographer availability information has been fetched successfully
   *
   * @param {Event} [event] - fetch success event.
   * */
  onFetchSuccessHandler(event) {
    const { slots } = event.detail;

    this._hideSpinner();
    this._resetSlotList();
    this._generateSlotList(slots);
  }

  /**
   * Event handler when a photographer availability information has NOT been fetched successfully
   *
   * @param {Event} _event - fetch failure event.
   * */
  onFetchFailureHandler(_event) {
    this._hideSpinner();
    this._showError();
  }

  /**
   * Event handler when an availability slot is selected
   *
   * @param {Event} [event] - custom event.
   * */
  onSelectSlotHandler(event) {
    this.state.slotSelected = true;
    this._enableFormSubmit();
  }

  /**
   * Event handler when an availability slot is unselected
   *
   * @param {Event} [event] - custom event.
   * */
  onUnselectSlotHandler(event) {
    this.state.slotSelected = false;
    this._disableFormSubmit();
  }

  /**
   * Event handler when the package category is changed
   * This is used to alter the category programmatically outside of the component scope
   *
   * @param {Event} [event] - custom event.
   * */
  onSelectCategoryHandler(event) {
    const { categoryId } = event.detail;

    this._togglePackageSelectVisibilityFor(categoryId);
    this._resetPackageFor(categoryId);
    this._fetchPhotographerAvailability();
  }

  /**
   * Event handler when the package is selected
   * This is used to alter the package programmatically outside of the component scope
   *
   * @param {Event} [event] - custom event.
   * */
  onSelectPackageHandler(event) {
    const { categoryId, packageId } = event.detail;

    this._togglePackageSelectVisibilityFor(categoryId);
    this._selectPackage(packageId);
    this._fetchPhotographerAvailability();
  }

  // Private

  _setup() {
    this.packageSelects.forEach(packageSelect => {
      new SelectInput(packageSelect);
    });

    !this.state.slotSelected && this._disableFormSubmit();
  }

  _addEventListeners() {
    this.elementRef.addEventListener(customEvent.PHOTOGRAPHER_AVAILABILITIES_IS_FETCHING, this.onFetchingHandler);
    this.elementRef.addEventListener(customEvent.PHOTOGRAPHER_AVAILABILITIES_FETCH_SUCCESS, this.onFetchSuccessHandler);
    this.elementRef.addEventListener(customEvent.PHOTOGRAPHER_AVAILABILITIES_FETCH_FAILURE, this.onFetchFailureHandler);
    this.elementRef.addEventListener(customEvent.PHOTOGRAPHER_AVAILABILITIES_SELECT_SLOT, this.onSelectSlotHandler);
    this.elementRef.addEventListener(customEvent.PHOTOGRAPHER_AVAILABILITIES_UNSELECT_SLOT, this.onUnselectSlotHandler);
    this.elementRef.addEventListener(customEvent.PHOTOGRAPHER_AVAILABILITIES_SELECT_CATEGORY, this.onSelectCategoryHandler);
    this.elementRef.addEventListener(customEvent.PHOTOGRAPHER_AVAILABILITIES_SELECT_PACKAGE, this.onSelectPackageHandler);

    this.calendarInput.addEventListener('change', this.changePackageOrDateHandler);
    this.packageSelects.forEach(packageSelect => {
      packageSelect.addEventListener('change', this.changePackageOrDateHandler);
    });
  }

  _addSlotClickEventListeners() {
    this.elementRef.querySelectorAll(SELECTOR['slotItem']).forEach(slotItem => {
      slotItem.addEventListener('click', this.onClickSlotItemHandler);
    });
  }

  _removeSlotClickEventListeners() {
    this.elementRef.querySelectorAll(SELECTOR['slotItem']).forEach(slotItem => {
      slotItem.removeEventListener('click', this.onClickSlotItemHandler);
    });
  }

  async _fetchPhotographerAvailability() {
    const fetchingEvent = new CustomEvent(customEvent.PHOTOGRAPHER_AVAILABILITIES_IS_FETCHING);
    this.elementRef.dispatchEvent(fetchingEvent);

    try {
      const photographerId = this.elementRef.querySelector(SELECTOR['photographerIdInput']).value;
      const packageId = this.packageSelects.filter(select => !select.attributes.disabled)[0].value;
      const date = this.calendarInput.value;

      const timetableSlots = await PhotographerAvailabilityAdapter.get(photographerId, packageId, date);

      const fetchSuccessEvent = new CustomEvent(customEvent.PHOTOGRAPHER_AVAILABILITIES_FETCH_SUCCESS, {
        detail: { slots: timetableSlots }
      });
      this.elementRef.dispatchEvent(fetchSuccessEvent);
    } catch (error) {
      const fetchFailureEvent = new CustomEvent(customEvent.PHOTOGRAPHER_AVAILABILITIES_FETCH_FAILURE, {
        detail: error
      });
      this.elementRef.dispatchEvent(fetchFailureEvent);
    }
  }

  _generateSlotList(timetableSlots) {
    this.slotList.innerHTML = timetableSlots.map(timetableSlot => {
      const slot = TimetableSlot({
        inputName: 'cart[appointment_date]',
        time: timetableSlot.start_time,
        timestamp: timetableSlot.start_timestamp,
        isPast: timetableSlot.is_past,
        isAvailable: timetableSlot.is_available
      });
      return slot.outerHTML;
    }).join('');

    this._addSlotClickEventListeners();
  }

  _resetSlotList() {
    this.slotList.innerHTML = '';
    this._removeSlotClickEventListeners();

    this.state.slotSelected = false;
  }

  /**
   * At any given time, only one list of package options is displayed
   * Hide / display the corresponding package option list for the current category
   *
   * @param {Integer} [categoryId] - category ID.
   * */
  _togglePackageSelectVisibilityFor(categoryId) {
    this.elementRef.querySelectorAll(SELECTOR['packageSelectInput']).forEach(selectInput => {
      const originalSelect = selectInput.previousElementSibling;

      if (originalSelect.dataset.categoryId === categoryId) {
        selectInput.removeAttribute('hidden');
        originalSelect.removeAttribute('disabled');
      } else {
        selectInput.setAttribute('hidden', 'hidden');
        originalSelect.setAttribute('disabled', 'disabled');
      }
    });
  }

  /**
   * Select the package in the custom select
   *
   * @param {Integer} [packageId] - package ID.
   * */
  _selectPackage(packageId) {
    const packageOption = this.elementRef.querySelector(`${SELECTOR['packageSelectInput']} li[rel="${packageId}"]`);

    packageOption.click();
  }

  /**
   * Reset the selected package option by picking the first package for a category
   *
   * @param {Integer} [categoryId] - category ID.
   * */
  _resetPackageFor(categoryId) {
    const originalSelect = this.packageSelects.filter(select => select.dataset.categoryId === categoryId)[0];
    const packageOptions = originalSelect.nextElementSibling.querySelectorAll('.select__options li');

    packageOptions[0].click();
  }

  _showSpinner() {
    this.elementRef.classList.add(CLASS_NAME['fetching']);
  }

  _hideSpinner() {
    this.elementRef.classList.remove(CLASS_NAME['fetching']);
  }

  _showError() {
    this.elementRef.classList.add(CLASS_NAME['error']);
  }

  _hideError() {
    this.elementRef.classList.remove(CLASS_NAME['error']);
  }

  _enableFormSubmit() {
    this.submitButton.disabled = false;
  }

  _disableFormSubmit() {
    this.submitButton.disabled = true;
  }
}

export default AvailabilityTimetableForm;



// WEBPACK FOOTER //
// ./engines/vcliq_booking/app/assets/javascripts/vcliq_booking/components/availabilityTimetableForm/index.js